/**
 * Resumo de DOC's - pagina do balancete Garcia Imoveis, com tabelas
 * estilizadas. Detecta colunas somaveis e alinha totais corretamente
 * (totais por coluna ficam embaixo da coluna que somam, nao no fim).
 */

import { ChipPagina } from "./PaginaUI";

interface GasAuditoriaUnidade {
  unidade: string;
  cobrado: number | null;
  devido: number | null;
  diferenca: number | null;
}
export interface GasAuditoria {
  mes_referencia: string;
  ciclo_id: number;
  aprovado: boolean;
  divergente: boolean;
  cobrado: number;
  devido: number;
  diferenca: number;
  diferenca_pct: number;
  tem_por_unidade: boolean;
  por_unidade: GasAuditoriaUnidade[];
}

interface Props {
  texto: string;
  pagina: string | null;
  competenciaId: number;
  onVerPagina: (pagina: number) => void;
  /** Auditoria gás cobrado × rateado (Controle de Gás). Null = sem comparação. */
  gasAuditoria?: GasAuditoria | null;
  /** Abre o Controle de Rateio do mês (uso opcional, a partir do alerta). */
  onAbrirGasRateio?: (mesReferencia: string) => void;
}

function brl(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function mesRefLabel(mesRef: string): string {
  // "2026-07" -> "07/2026"
  const m = /^(\d{4})-(\d{2})$/.exec(mesRef);
  return m ? `${m[2]}/${m[1]}` : mesRef;
}

/** Alerta de divergência do gás, exibido BEM no topo do relatório. */
function GasDivergenciaBanner({ audit, onAbrir }: {
  audit: GasAuditoria;
  onAbrir?: (mesReferencia: string) => void;
}) {
  if (!audit.divergente) return null;
  const cobrouMenos = audit.diferenca < 0;
  return (
    <div className="mb-4 rounded-xl border-2 border-rose-300 bg-rose-50 p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="text-2xl leading-none" aria-hidden>⚠️</span>
        <div className="flex-1">
          <h4 className="text-sm font-bold text-rose-800">
            Divergência no rateio do gás (ciclo {mesRefLabel(audit.mes_referencia)})
          </h4>
          <p className="text-sm text-rose-900 mt-1">
            O valor cobrado dos condôminos neste balancete{" "}
            <strong>não bate</strong> com o rateio do Controle de Gás para o ciclo{" "}
            {mesRefLabel(audit.mes_referencia)}
            {audit.aprovado ? " (rateio aprovado)" : " (prévia — rateio ainda não aprovado)"}.
          </p>
          <div className="mt-2 grid grid-cols-3 gap-2 text-xs max-w-md">
            <div className="rounded-md bg-white/70 border border-rose-200 px-2 py-1.5">
              <div className="text-rose-500 uppercase tracking-wide text-[10px] font-semibold">Cobrado (balancete)</div>
              <div className="text-rose-900 font-bold tabular-nums">{brl(audit.cobrado)}</div>
            </div>
            <div className="rounded-md bg-white/70 border border-rose-200 px-2 py-1.5">
              <div className="text-rose-500 uppercase tracking-wide text-[10px] font-semibold">Devido (rateio)</div>
              <div className="text-rose-900 font-bold tabular-nums">{brl(audit.devido)}</div>
            </div>
            <div className="rounded-md bg-white/70 border border-rose-200 px-2 py-1.5">
              <div className="text-rose-500 uppercase tracking-wide text-[10px] font-semibold">Diferença</div>
              <div className="text-rose-900 font-bold tabular-nums">
                {brl(audit.diferenca)} ({audit.diferenca_pct}%)
              </div>
            </div>
          </div>
          <p className="text-xs text-rose-700 mt-2">
            {cobrouMenos
              ? "A administradora cobrou a MENOS do que o devido — a diferença tende a ser bancada pelo caixa do condomínio."
              : "A administradora cobrou a MAIS do que o rateio calculado — verifique."}
          </p>
          {onAbrir && (
            <button
              type="button"
              onClick={() => onAbrir(audit.mes_referencia)}
              className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700"
            >
              Verificar no Controle de Rateio →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const SPLIT = /\s{2,}/;
const RE_NUMERO_BR = /^-?\d[\d\.\s]*,\d{2}(?:\s*\(\d+\))?$/;
const RE_NEGATIVO = /^-/;
const RE_RODAPE_PDF = /^\d{2}\/\d{2}\/\d{4}\s+\d{2}:\d{2}/;
const RE_TITULO_PDF = /RESUMO\s+DE\s+DOC.{0,3}S\s+DE\s+CONDOM/i;
const RE_INCERTO = /\s*[\[\(]\?\s*[\]\)]\s*$/;

interface Bloco {
  tipo: "tabela" | "paragrafo";
  titulo?: string;
  header?: string[];
  rows?: string[][];
  rodape?: string[][];
  notas?: string[][];
  texto?: string;
}
interface Chip { label: string; valor: string }

function limparCelula(c: string): string { return c.replace(RE_INCERTO, "").trim(); }
function ehNumero(s: string): boolean { return RE_NUMERO_BR.test(s.trim()); }
function ehNegativo(s: string): boolean { return RE_NEGATIVO.test(s.trim()); }

function extrairChips(texto: string): { chips: Chip[]; condominio: string | null; bloco: string | null } {
  const chips: Chip[] = [];
  let condominio: string | null = null;
  let bloco: string | null = null;
  const re = /(Emiss[aã]o|Tipo\s+DOC|Compet[eê]ncia|Vencimento)\s*:\s*([^\s][^\n]*?)(?=(?:\s{2,}|$))/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(texto)) !== null) {
    const label = m[1].replace(/\s+/g, " ").replace(/^./, (c) => c.toUpperCase());
    chips.push({ label, valor: m[2].trim() });
  }
  const mc = texto.match(/Condom[ií]nio\s*:\s*\d+\s+([^\n]+?)(?:\s{2,}|$)/i);
  if (mc) condominio = mc[1].trim();
  const mb = texto.match(/Bloco\s*:\s*([^\s][^\n]*?)(?:\s{2,}|$)/i);
  if (mb) bloco = mb[1].trim();
  return { chips, condominio, bloco };
}

// Parser estruturado para quando o OCR separa colunas por espaço simples
// (o split por 2+ espaços falha). Ancorado na ESTRUTURA conhecida de cada
// tabela do "Resumo de DOC's" Garcia Imóveis, então funciona com 1 ou 2+
// espaços. Retorna null se não conseguir extrair linhas (cai no fallback).
const RE_UNIT_ROW = new RegExp(
  "^(.+?)\\s+(-?[\\d.]+,\\d{2})\\s+(-?[\\d.]+,\\d{2})\\s+(-?[\\d.]+,\\d{2})\\s+(-?[\\d.]+,\\d{2})\\s+(\\d{7,})\\s+(-?[\\d.]+,\\d{2})$",
);
const RE_TAXA_ROW = /^(\d{2,4})\s+(.+?)\s+(-?[\d.]+,\d{2})$/;
const RE_BOLETO_ROW = new RegExp(
  "^(.+?)\\s+([\\d.]+,\\d{2}\\s*\\(\\d+\\))\\s+([\\d.]+,\\d{2}\\s*\\(\\d+\\))\\s+([\\d.]+,\\d{2}\\s*\\(\\d+\\))$",
);

function parsearTabelaEstruturada(titulo: string, linhas: string[]): Bloco | null {
  if (titulo === "Detalhamento por unidade") {
    const header = ["Economia", "Tipo", "Consumo Gás", "Condomínio", "Consumo D'Água", "Devolução", "Nosso Número", "Total"];
    const rows: string[][] = [];
    const rodape: string[][] = [];
    for (const l of linhas) {
      const m = l.match(RE_UNIT_ROW);
      if (m) {
        const lbl = m[1].trim();
        const mm = lbl.match(/^(\S+)\s*(.*)$/);
        const economia = mm ? mm[1] : lbl;
        const tipo = mm && mm[2] ? mm[2] : "AP";
        rows.push([economia, tipo, m[2], m[3], m[4], m[5], m[6], m[7]]);
      } else {
        const toks = l.split(/\s+/).filter(Boolean);
        const money = toks.filter((t) => /^-?[\d.]+,\d{2}$/.test(t));
        if (money.length >= 3 && money.length === toks.length) rodape.push(money);
      }
    }
    if (rows.length === 0) return null;
    return { tipo: "tabela", titulo, header, rows, rodape: rodape.length ? rodape : undefined };
  }
  if (titulo === "Resumo de Taxas") {
    const header = ["Taxa", "Descrição", "Valor"];
    const rows: string[][] = [];
    const rodape: string[][] = [];
    for (const l of linhas) {
      const m = l.match(RE_TAXA_ROW);
      if (m) rows.push([m[1], m[2].trim(), m[3]]);
      else if (/^QTDE/i.test(l)) {
        const mv = l.match(/(-?[\d.]+,\d{2})\s*$/);
        rodape.push(["TOTAL", mv ? mv[1] : ""]);
      }
    }
    if (rows.length === 0) return null;
    return { tipo: "tabela", titulo, header, rows, rodape: rodape.length ? rodape : undefined };
  }
  if (titulo === "Resumo de Boletos Emitidos x Quitados") {
    const header = ["Bloco", "Emitidos", "Quitados", "Inadimplentes"];
    const rows: string[][] = [];
    for (const l of linhas) {
      const m = l.match(RE_BOLETO_ROW);
      if (m) rows.push([m[1].trim(), m[2].replace(/\s+/g, " "), m[3].replace(/\s+/g, " "), m[4].replace(/\s+/g, " ")]);
    }
    if (rows.length === 0) return null;
    return { tipo: "tabela", titulo, header, rows };
  }
  return null;
}

function parsear(texto: string): Bloco[] {
  const linhas = texto.split("\n");
  const blocos: Bloco[] = [];
  let acc: string[] = [];
  const HEADERS = [
    { re: /^\s*Economia\s+Tipo\s+/i, titulo: "Detalhamento por unidade" },
    { re: /^\s*Taxa\s+Descri/i, titulo: "Resumo de Taxas" },
    { re: /^\s*Bloco\s+Emit/i, titulo: "Resumo de Boletos Emitidos x Quitados" },
  ];
  const SEP = /^[-=]{3,}/;
  function flush() {
    const t = acc.join("\n").trim();
    if (t) blocos.push({ tipo: "paragrafo", texto: t });
    acc = [];
  }
  let i = 0;
  while (i < linhas.length) {
    const ln = linhas[i];
    const lnT = ln.trim();
    if (!lnT) { acc.push(""); i++; continue; }
    if (SEP.test(lnT)) { acc.push(lnT); i++; continue; }
    const mh = HEADERS.find((h) => h.re.test(ln));
    if (mh) {
      const header = lnT.split(SPLIT).filter(Boolean).map(limparCelula);
      // Quando o OCR separa colunas por espaço simples, o header colapsa em
      // 1 célula e o split por 2+ espaços falha. Nesse caso usamos um parser
      // estruturado (ancorado na estrutura de cada tabela), que monta a
      // tabela mesmo com espaço simples. Se não conseguir, cai em parágrafo.
      if (header.length < 3) {
        flush();
        const raw: string[] = [];
        i++;
        let vaziasS = 0;
        while (i < linhas.length) {
          const l2 = linhas[i];
          const t2 = l2.trim();
          if (!t2) { vaziasS++; if (vaziasS >= 2) break; i++; continue; }
          vaziasS = 0;
          if (HEADERS.find((h) => h.re.test(l2))) break;
          if (SEP.test(t2)) break;
          if (RE_RODAPE_PDF.test(t2)) break;
          raw.push(t2);
          i++;
        }
        const tab = parsearTabelaEstruturada(mh.titulo, raw);
        if (tab) blocos.push(tab);
        else blocos.push({ tipo: "paragrafo", texto: [lnT, ...raw].join("\n") });
        continue;
      }
      flush();
      const rows: string[][] = [];
      const rodape: string[][] = [];
      const notas: string[][] = [];
      i++;
      let vazias = 0;
      while (i < linhas.length) {
        const l2 = linhas[i];
        const t2 = l2.trim();
        if (!t2) { vazias++; if (vazias >= 2) break; i++; continue; }
        vazias = 0;
        if (HEADERS.find((h) => h.re.test(l2))) break;
        if (SEP.test(t2)) break;
        if (RE_RODAPE_PDF.test(t2)) break;
        const cells = t2.split(SPLIT).filter(Boolean).map(limparCelula);
        const rod = /^(QTDE|TOTAL|Inadimplentes|\d[\d\.]*,\d{2}\b)/i.test(t2);
        const limite = Math.max(3, Math.ceil(header.length / 2));
        if (cells.length < limite && !rod) notas.push(cells);
        else if (rod) rodape.push(cells);
        else rows.push(cells);
        i++;
      }
      blocos.push({
        tipo: "tabela", titulo: mh.titulo, header, rows,
        rodape: rodape.length > 0 ? rodape : undefined,
        notas: notas.length > 0 ? notas : undefined,
      });
      continue;
    }
    acc.push(ln);
    i++;
  }
  flush();
  return blocos;
}

function classeCelula(c: string, primeira: boolean): string {
  const base = "px-3 py-2.5";
  if (ehNumero(c)) {
    if (ehNegativo(c)) return `${base} text-right tabular-nums text-rose-700 font-medium`;
    return `${base} text-right tabular-nums text-slate-800`;
  }
  if (primeira) return `${base} text-left font-semibold text-slate-900`;
  return `${base} text-left text-slate-600`;
}
function classeRodape(c: string): string {
  const base = "px-3 py-2.5";
  if (ehNumero(c)) {
    if (ehNegativo(c)) return `${base} text-right tabular-nums text-rose-700 font-bold`;
    return `${base} text-right tabular-nums text-slate-900 font-bold`;
  }
  return `${base} text-left text-slate-700 font-medium`;
}

// Detecta quais colunas do header tem dados monetarios em TODAS as rows.
// Util para alinhar totais por coluna (rodape) corretamente.
function colunasMonetarias(rows: string[][], nCols: number): number[] {
  if (rows.length === 0) return [];
  const cols: number[] = [];
  for (let j = 0; j < nCols; j++) {
    if (rows.every((r) => r[j] !== undefined && ehNumero(r[j]))) cols.push(j);
  }
  return cols;
}

// Distribui valores de uma row de rodape nas colunas monetarias.
// Estrategia: se o numero de valores bate com (colsMon - 1) e os valores sao
// numericos, mapeia em ordem; senao, mantem comportamento simples (colSpan
// na primeira pra empurrar pra direita).
function mapearRodape(row: string[], colsMonetarias: number[]): { valor: string; col: number }[] | null {
  const valores = row.filter((v) => ehNumero(v));
  if (valores.length === 0 || colsMonetarias.length === 0) return null;
  // Casa os totais com as colunas MONETÁRIAS:
  //  - mesma quantidade  → 1:1 (totais cobrem todas as colunas, inclusive Total);
  //  - uma a menos       → ignora a última (linha de totais sem o total geral);
  //  - menos que isso    → alinha às ÚLTIMAS colunas monetárias (totais à direita).
  let colsAlvo: number[];
  if (valores.length === colsMonetarias.length) {
    colsAlvo = colsMonetarias;
  } else if (valores.length === colsMonetarias.length - 1) {
    colsAlvo = colsMonetarias.slice(0, -1);
  } else if (valores.length < colsMonetarias.length) {
    colsAlvo = colsMonetarias.slice(colsMonetarias.length - valores.length);
  } else {
    return null;
  }
  return valores.map((v, idx) => ({ valor: v, col: colsAlvo[idx] }));
}

function todosQuitados(bloco: Bloco): boolean {
  if (bloco.titulo !== "Resumo de Boletos Emitidos x Quitados") return false;
  if (!bloco.rows || bloco.rows.length === 0) return false;
  return bloco.rows.every((row) => {
    if (row.length < 3) return false;
    const norm = (s: string) => s.replace(/\s+/g, "");
    return norm(row[1]) === norm(row[2]);
  });
}

export function ResumoDocsSection({ texto, pagina, onVerPagina, gasAuditoria, onAbrirGasRateio }: Props) {
  let blocos: Bloco[] = [];
  try { blocos = parsear(texto); } catch { blocos = [{ tipo: "paragrafo", texto }]; }
  const temTabela = blocos.some((b) => b.tipo === "tabela");
  const { chips, condominio, bloco } = extrairChips(texto);

  return (
    <section className="mt-8">
      {gasAuditoria && (
        <GasDivergenciaBanner audit={gasAuditoria} onAbrir={onAbrirGasRateio} />
      )}
      <div className="rounded-t-xl bg-gradient-to-r from-blue-900 to-blue-700 px-5 py-4 text-white shadow-sm">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-lg font-semibold">Resumo de DOC{"'"}s</h3>
            <p className="text-xs text-blue-100/90 mt-0.5">Detalhamento da arrecadacao por unidade</p>
          </div>
          {pagina ? (
            <span className="text-xs text-blue-100 bg-blue-800/40 border border-blue-500/30 px-2 py-1 rounded inline-flex items-center gap-1.5">
              Origem: <ChipPagina fonte={pagina} onAbrir={onVerPagina} />
            </span>
          ) : null}
        </div>
        {(condominio || bloco) && (
          <p className="text-sm text-blue-50 mt-2 font-medium">
            {condominio}
            {condominio && bloco ? <span className="text-blue-200/80"> · Bloco {bloco}</span> : null}
            {!condominio && bloco ? <>Bloco {bloco}</> : null}
          </p>
        )}
      </div>
      {chips.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-slate-50 border-x border-slate-200 px-5 py-3">
          {chips.map((chip, i) => (
            <div key={i} className="text-xs">
              <div className="text-slate-500 uppercase tracking-wide text-[10px] font-semibold">{chip.label}</div>
              <div className="text-slate-900 font-medium tabular-nums mt-0.5">{chip.valor}</div>
            </div>
          ))}
        </div>
      )}
      <div className="border border-slate-200 border-t-0 rounded-b-xl overflow-hidden bg-white">
        {!temTabela ? (
          <pre className="text-xs font-mono text-slate-700 p-4 whitespace-pre leading-relaxed overflow-x-auto">{texto}</pre>
        ) : (
          <div className="divide-y divide-slate-100">
            {blocos.map((b, idx) => {
              if (b.tipo === "paragrafo" && b.texto) {
                const linhasUteis = b.texto.split("\n").filter((l) => {
                  const t = l.trim();
                  if (!t) return false;
                  if (/^[-=]{3,}/.test(t)) return false;
                  if (RE_TITULO_PDF.test(t)) return false;
                  if (/^(Emiss[aã]o|Tipo\s+DOC|Compet[eê]ncia|Vencimento|Condom[ií]nio|Bloco)\s*:/i.test(t)) return false;
                  if (t === "GARCIA" || t === "IMÓVEIS" || /^Garcia\s+Im/i.test(t)) return false;
                  return true;
                }).join("\n");
                if (!linhasUteis.trim()) return null;
                return (
                  <div key={idx} className="px-5 py-3">
                    <pre className="text-[11px] font-mono text-slate-500 whitespace-pre-wrap leading-relaxed">{linhasUteis}</pre>
                  </div>
                );
              }
              if (b.tipo === "tabela" && b.header) {
                const nCols = b.header.length;
                const rowsData = b.rows ?? [];
                const colsMon = colunasMonetarias(rowsData, nCols);
                const quitados = todosQuitados(b);
                return (
                  <div key={idx} className="px-5 py-4">
                    <div className="flex items-center justify-between mb-2.5">
                      <h4 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">{b.titulo}</h4>
                      {quitados ? (<span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">✓ 100% quitado</span>) : null}
                    </div>
                    <div className="rounded-lg border border-slate-200 overflow-hidden overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-slate-800 text-slate-100 text-xs uppercase tracking-wider">
                          <tr>
                            {b.header.map((h, j) => (
                              <th key={j} className={`px-3 py-2.5 font-semibold ${j === 0 ? "text-left" : "text-right"}`}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rowsData.map((row, k) => (
                            <tr key={k} className={`${k % 2 === 0 ? "bg-white" : "bg-slate-50/60"} hover:bg-blue-50/40 transition-colors`}>
                              {row.map((c, j) => (<td key={j} className={classeCelula(c, j === 0)}>{c}</td>))}
                            </tr>
                          ))}
                        </tbody>
                        {b.rodape && b.rodape.length > 0 ? (
                          <tfoot className="bg-blue-50 border-t-2 border-blue-200">
                            {b.rodape.map((row, k) => {
                              const mapeado = mapearRodape(row, colsMon);
                              if (mapeado) {
                                // Renderiza cada col 0..nCols-1: se eh alvo, pinta valor; senao, vazio
                                const valoresPorCol: Record<number, string> = {};
                                mapeado.forEach((it) => { valoresPorCol[it.col] = it.valor; });
                                const cells: { conteudo: string; colSpan: number }[] = [];
                                let j = 0;
                                while (j < nCols) {
                                  if (valoresPorCol[j] !== undefined) {
                                    cells.push({ conteudo: valoresPorCol[j], colSpan: 1 });
                                    j++;
                                  } else {
                                    let span = 0;
                                    while (j + span < nCols && valoresPorCol[j + span] === undefined) span++;
                                    cells.push({ conteudo: "", colSpan: span });
                                    j += span;
                                  }
                                }
                                return (
                                  <tr key={k}>
                                    {cells.map((cell, idx2) => (
                                      <td key={idx2} colSpan={cell.colSpan} className={cell.conteudo ? classeRodape(cell.conteudo) : "px-3 py-2.5"}>
                                        {cell.conteudo}
                                      </td>
                                    ))}
                                  </tr>
                                );
                              }
                              // Fallback: comportamento simples (colSpan na primeira)
                              const cs = row.length < nCols ? nCols - row.length + 1 : 1;
                              return (
                                <tr key={k}>
                                  {row.map((c, j) => (<td key={j} colSpan={j === 0 ? cs : 1} className={classeRodape(c)}>{c}</td>))}
                                </tr>
                              );
                            })}
                          </tfoot>
                        ) : null}
                      </table>
                    </div>
                    {b.notas && b.notas.length > 0 ? (
                      <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                        <div className="font-semibold mb-1">⚠ Lancamento(s) sem identificacao completa no PDF:</div>
                        <ul className="space-y-0.5">
                          {b.notas.map((row, k) => (<li key={k} className="font-mono tabular-nums">{row.join(" · ")}</li>))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                );
              }
              return null;
            })}
          </div>
        )}
      </div>
    </section>
  );
}
